import { useState } from 'react';
import { Check, Sparkles } from 'lucide-react';
import type { NomeFonte } from '@miolos/core';
import {
  CATEGORIA_FONTE,
  DESCRICAO_FONTE_NOMEADA,
  ROTULO_CATEGORIA_FONTE,
  ROTULO_FONTE_NOMEADA,
  fontesPorCategoria,
} from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Campo } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { familiaDaFonte } from '@/impressao/cores';
import { cn } from '@/lib/utils';

interface Props {
  /** Qual dos dois papéis esta escolha está mudando. */
  papel: 'TITULO' | 'TEXTO';
  valor: NomeFonte;
  aoEscolher: (fonte: NomeFonte) => void;
}

/**
 * Escolha da fonte, pelo nome.
 *
 * São dois caminhos de propósito:
 *
 * - a **lista**, agrupada por clima e leve, que é o caminho de quem já sabe o que
 *   quer (o nome aparece na letra do sistema: abrir a lista não baixa arquivo
 *   nenhum);
 * - a **galeria**, onde cada fonte aparece escrita nela mesma — é onde a escolha
 *   acontece de verdade, porque ninguém sabe o que é "Quicksand" até ver. Abrir
 *   a galeria baixa as dezoito fontes (~620 KB, uma vez só, e ficam em cache),
 *   então ela é um clique consciente, e não o estado inicial da tela.
 */
export function EscolhaDeFonte({ papel, valor, aoEscolher }: Props) {
  const [galeriaAberta, setGaleriaAberta] = useState(false);
  const grupos = fontesPorCategoria();
  const rotulo = papel === 'TITULO' ? 'Fonte dos títulos' : 'Fonte do texto';
  const dica =
    papel === 'TITULO'
      ? 'O número do dia, o nome do mês e os títulos em destaque.'
      : 'Rótulos, quadros, conteúdo do dia e cadernos.';

  return (
    <div className="space-y-2">
      <Campo rotulo={rotulo} dica={dica}>
        <Select value={valor} onValueChange={(escolhida) => aoEscolher(escolhida as NomeFonte)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {grupos.map((grupo) => (
              <SelectGroup key={grupo.categoria}>
                <SelectLabel>{grupo.rotulo}</SelectLabel>
                {grupo.fontes.map((fonte) => (
                  <SelectItem key={fonte} value={fonte}>
                    {ROTULO_FONTE_NOMEADA[fonte]}
                    {fonte === 'SISTEMA' ? '' : ` · ${ROTULO_CATEGORIA_FONTE[CATEGORIA_FONTE[fonte]].toLowerCase()}`}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      </Campo>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="contorno" tamanho="pequeno" onClick={() => setGaleriaAberta(true)}>
          <Sparkles />
          Ver as fontes
        </Button>
        <span className="text-[11px] leading-relaxed text-muted-foreground">
          {ROTULO_FONTE_NOMEADA[valor]} — {DESCRICAO_FONTE_NOMEADA[valor]}
        </span>
      </div>

      <Dialog open={galeriaAberta} onOpenChange={setGaleriaAberta}>
        <DialogContent
          titulo={`Fontes para ${papel === 'TITULO' ? 'títulos' : 'texto'}`}
          descricao="Toque numa fonte para usar. Cada uma aparece escrita nela mesma, com uma linha de amostra."
          className="max-w-3xl"
        >
          <div className="space-y-5">
            {grupos.map((grupo) => (
              <section key={grupo.categoria} className="space-y-2">
                <div className="space-y-0.5">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {grupo.rotulo}
                  </h4>
                  <p className="text-[11px] leading-relaxed text-muted-foreground">{grupo.descricao}</p>
                </div>

                <ul className="grid gap-2 sm:grid-cols-2">
                  {grupo.fontes.map((fonte) => (
                    <li key={fonte}>
                      <button
                        type="button"
                        onClick={() => {
                          aoEscolher(fonte);
                          setGaleriaAberta(false);
                        }}
                        aria-pressed={fonte === valor}
                        className={cn(
                          'w-full space-y-1 rounded-md border px-3 py-2 text-left transition-colors',
                          fonte === valor
                            ? 'border-primary bg-primary/[0.06]'
                            : 'border-border hover:border-primary/40 hover:bg-secondary/60',
                        )}
                      >
                        <span className="flex items-center justify-between gap-2">
                          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            {ROTULO_FONTE_NOMEADA[fonte]}
                          </span>
                          {fonte === valor ? <Check className="size-3.5 shrink-0 text-primary" strokeWidth={3} /> : null}
                        </span>
                        <span
                          className="block truncate text-lg leading-tight text-foreground"
                          style={{ fontFamily: familiaDaFonte(fonte) }}
                        >
                          AGENDA · Setembro 2026
                        </span>
                        <span className="block text-[11px] leading-relaxed text-muted-foreground">
                          {DESCRICAO_FONTE_NOMEADA[fonte]}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}

            <p className="flex items-start gap-2 rounded-md border border-border bg-muted/50 p-2.5 text-[11px] leading-relaxed text-muted-foreground">
              <Badge tom="principal" className="shrink-0">
                PDF
              </Badge>
              <span>
                Todas as fontes viajam <strong>dentro do PDF</strong>: a gráfica imprime a mesma letra sem
                depender de internet, e a fonte aqui só é baixada quando o miolo a usa.
              </span>
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
